import { useEffect, useState } from 'react';
import { getAccessToken } from '../services/auth';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

/**
 * Module-scope cache keyed by `${brandId}:${size}` → blob URL. Keeps a single
 * object URL per brand/size across re-mounts so the sidebar doesn't thrash
 * fetch + URL.createObjectURL on every selector open.
 *
 * Invalidated via `invalidateBrandFavicon(brandId)` after a refresh call —
 * callers that force a re-fetch should clear both 64 and 128 entries.
 */
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

async function fetchBlob(brandId: string, size: 64 | 128): Promise<string | null> {
  const token = await getAccessToken();
  const res = await fetch(`${API_BASE}/brands/${brandId}/favicon?size=${size}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  const contentType = res.headers.get('Content-Type') || '';
  if (!contentType.startsWith('image/')) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Returns a ready-to-use `<img src>` value for the brand's stored favicon, or
 * null while loading / when the brand has no favicon yet. Auto-triggers a
 * lazy backfill (POST /favicon/refresh) on 404.
 */
export function useBrandFavicon(
  brandId: string | undefined,
  options: { size?: 64 | 128; url?: string } = {},
): string | null {
  const size = options.size ?? 64;
  const key = brandId ? `${brandId}:${size}` : '';
  const [src, setSrc] = useState<string | null>(brandId ? cache.get(key) || null : null);

  useEffect(() => {
    if (!brandId) return;
    const cached = cache.get(key);
    if (cached) {
      setSrc(cached);
      return;
    }
    let cancelled = false;

    const inflightPromise = inflight.get(key);
    const load = inflightPromise
      ? inflightPromise
      : (async () => {
          const direct = await fetchBlob(brandId, size);
          if (direct) return direct;
          // 404 → lazy backfill, then retry once.
          const token = await getAccessToken();
          await fetch(`${API_BASE}/brands/${brandId}/favicon/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: '{}',
          }).catch(() => null);
          return fetchBlob(brandId, size);
        })();

    inflight.set(key, load);
    load
      .then((url) => {
        if (cancelled) return;
        if (url) {
          cache.set(key, url);
          setSrc(url);
        } else {
          setSrc(null);
        }
      })
      .finally(() => inflight.delete(key));

    return () => {
      cancelled = true;
    };
  }, [brandId, size, key]);

  return src;
}

export function invalidateBrandFavicon(brandId: string): void {
  for (const size of [64, 128] as const) {
    const key = `${brandId}:${size}`;
    const existing = cache.get(key);
    if (existing) URL.revokeObjectURL(existing);
    cache.delete(key);
  }
}
